import { createFileRoute, Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { Badge, type BadgeVariant } from '~/components/ui/Badge';
import { StudentListHeader } from '~/components/ui/ContentNav';
import { PageTitle } from '~/components/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '~/components/ui/Pagination';
import { Table } from '~/components/ui/Table';
import { useStudentFocus } from '~/modules/studentFocus';

type CertificateSummary = components['schemas']['CertificateSummary'];

type CertificatesSearch = {
  user_id?: number;
};

export const Route = createFileRoute('/_admin/certificates/')({
  component: CertificatesList,
  validateSearch: (search: Record<string, unknown>): CertificatesSearch => {
    const raw = search.user_id;
    const id =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw !== ''
          ? Number(raw)
          : undefined;
    return {
      user_id: id !== undefined && Number.isFinite(id) ? id : undefined,
    };
  },
});

const columnHelper = createColumnHelper<CertificateSummary>();

function typeBadge(type: string): { label: string; variant: BadgeVariant } {
  return type === 'series'
    ? { label: 'Series', variant: 'warning' }
    : { label: 'Course', variant: 'neutral' };
}

const columns = [
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('user_display_name', {
    header: 'Student',
    cell: (info) => (
      <Link
        to="/students/$studentId"
        params={{ studentId: String(info.row.original.user_id) }}
        style={{ color: 'var(--blue)', textDecoration: 'none' }}
      >
        {info.getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor('template_title', { header: 'Certificate' }),
  columnHelper.accessor('type', {
    header: 'Type',
    cell: (info) => {
      const { label, variant } = typeBadge(info.getValue());
      return <Badge $variant={variant}>{label}</Badge>;
    },
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: (info) =>
      info.row.original.is_pending ? (
        <Badge $variant="warning">Pending</Badge>
      ) : (
        <Badge $variant="success">Awarded</Badge>
      ),
  }),
  columnHelper.accessor('awarded_at', {
    header: 'Awarded',
    cell: (info) => {
      const v = info.getValue();
      return v ? new Date(v).toLocaleString() : '—';
    },
  }),
];

function CertificatesList() {
  const search = Route.useSearch();
  const [page, setPage] = useState(0);
  const { focusedStudent } = useStudentFocus();

  const effectiveUserId = search.user_id ?? focusedStudent?.id;

  const { data, isLoading, error } = api.listCertificates.useQuery({
    query: {
      limit: LIST_PAGE_SIZE,
      offset: LIST_PAGE_SIZE * page,
      ...(effectiveUserId !== undefined ? { user_id: effectiveUserId } : {}),
    },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      {search.user_id !== undefined ? (
        <StudentListHeader studentId={search.user_id} title="Certificates" />
      ) : (
        <PageTitle>Certificates</PageTitle>
      )}

      {isLoading ? (
        <LoadingMessage>Loading Certificates...</LoadingMessage>
      ) : error ? (
        <ErrorMessage>Error loading Certificates</ErrorMessage>
      ) : (
        <>
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
          />

          <Table table={table} />
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
            hideWhenAlone
          />
        </>
      )}
    </div>
  );
}

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--grey-600);
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--error);
`;
